import { useState, useEffect, useRef } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase, safeSubscribe } from '@/integrations/supabase/client';
import { Profile, RoleText } from '../types';
import { toast } from 'sonner';
import { logAuthEvent } from '@/lib/logging';
import { logger, criticalLogger } from '@/utils/logger';

interface UseProfileLoaderReturn {
  profile: Profile | null;
  roles: RoleText[];
  isLoadingProfile: boolean;
  refreshProfile: () => Promise<void>;
  isAdminUser: boolean;
}

/**
 * Custom hook to manage profile loading with timeout fallback
 * Ensures profile loading always completes, even on slow networks or Supabase timeouts
 */
export const useProfileLoader = (
  session: Session | null,
  correlationId: string
): UseProfileLoaderReturn => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<RoleText[]>([]);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  

  // Function to refresh profile data with session validation and retry logic
  const refreshProfile = async (retryCount = 0) => {
    if (!session?.user?.id) {
      logger.log('[useProfileLoader] No user session for profile refresh');
      setIsAdminUser(false);
      return;
    }

    // Validate session is still active
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession || currentSession.user.id !== session.user.id) {
        logger.warn('[useProfileLoader] Session invalid or changed, skipping profile refresh');
        return;
      }
    } catch (sessionError) {
      criticalLogger.error('[useProfileLoader] Session validation failed:', sessionError);
      return;
    }

    logger.log('[useProfileLoader] Refreshing profile for user:', session.user.id, `(attempt ${retryCount + 1})`);
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .maybeSingle();

      if (error) {
        // Handle specific error types
        if (error.code === 'PGRST116' && retryCount < 2) {
          logger.warn('[useProfileLoader] Profile not found, retrying...', error);
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
          return refreshProfile(retryCount + 1);
        }
        
        if (error.message?.includes('JWT') || error.message?.includes('token') && retryCount < 1) {
          logger.warn('[useProfileLoader] Token issue, refreshing session and retrying...', error);
          await supabase.auth.refreshSession();
          await new Promise(resolve => setTimeout(resolve, 500));
          return refreshProfile(retryCount + 1);
        }

        criticalLogger.error('[useProfileLoader] Error refreshing profile:', error);
        setIsAdminUser(false);
        return;
      }

      // Check admin status using direct app_admins table query with known user ID
      const { data: adminCheck, error: adminError } = await supabase
        .from('app_admins')
        .select('user_id')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (adminError && adminError.code !== 'PGRST116') {
        criticalLogger.error('[useProfileLoader] Error checking admin status:', adminError);
      }

      const isAdmin = adminCheck !== null;
      logger.log('[useProfileLoader] Admin status:', isAdmin);
      setIsAdminUser(isAdmin);

      setProfile(data);
      logger.log('[useProfileLoader] Profile refreshed successfully:', data);
    } catch (error) {
      // Handle critical errors that might cause sign-out
      if (error instanceof Error) {
        if (error.message?.includes('JWT') || 
            error.message?.includes('token') || 
            error.message?.includes('session')) {
          criticalLogger.error('[useProfileLoader] Auth-related error during profile refresh:', error);
          // Don't throw or propagate auth errors that could trigger sign-out
          return;
        }
      }
      
      criticalLogger.error('[useProfileLoader] Unexpected error refreshing profile:', error);
      setIsAdminUser(false);
    }
  };

  // Auto-create profile if it doesn't exist
  const createProfile = async (userId: string, email: string): Promise<Profile | null> => {
    logger.log('[useProfileLoader] Creating profile for user:', { userId, email });
    // Handle Apple ID fallback emails by creating a proper username
    const fallbackEmail = email || `user_${userId}@apple.private`;
    const username = fallbackEmail.includes('@apple.private') 
      ? `user_${userId.slice(0, 8)}` // Short username for Apple ID users
      : fallbackEmail.split('@')[0];
    
    const { data, error } = await supabase
      .from('profiles')
      .insert({
        id: userId,
        email: fallbackEmail,
        username: username,
        role_text: 'free' as RoleText,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) {
      criticalLogger.error('[useProfileLoader] Error creating profile:', error);
      return null;
    }

    logger.log('[useProfileLoader] Profile created successfully:', data);
    return data as Profile;
  };

  // Profile loading effect with timeout fallback
  useEffect(() => {
    logger.log('[useProfileLoader] === PROFILE LOADING EFFECT ===');
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!session?.user) {
      logger.log('[useProfileLoader] No session user, skipping profile operations');
      setIsLoadingProfile(false);
      return;
    }

    const userId = session.user.id;
    const userEmail = session.user.email || `user_${session.user.id}@apple.private`;

    logger.log('[useProfileLoader] Session user detected, fetching profile:', { userId, userEmail, originalEmail: session.user.email });
    logAuthEvent('profile_fetch_start', { 
      userId,
      correlationId,
      component: 'useProfileLoader'
    });

    const fetchOrCreateProfile = async () => {
      setIsLoadingProfile(true);
      logger.log('[useProfileLoader] Starting profile fetch/create process');
      
      try {
        // First try to fetch existing profile
        logger.log('[useProfileLoader] Fetching profile for user:', userId);
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (error) {
          criticalLogger.error('[useProfileLoader] Profile fetch error:', error);
          criticalLogger.error('[useProfileLoader] Full error details:', { 
            message: error.message, 
            code: error.code, 
            details: error.details, 
            hint: error.hint 
          });
          logAuthEvent('profile_fetch_error', { 
            userId, 
            error: error.message,
            correlationId,
            component: 'useProfileLoader'
          });
          setRoles(['free']);
          
          toast.error(`Failed to load profile: ${error.message}`);
        } else if (!data) {
          // Profile doesn't exist, create it
          logger.log('[useProfileLoader] Profile not found, creating new profile for user:', userId);
          const newProfile = await createProfile(userId, userEmail);
          
          if (newProfile) {
            setProfile(newProfile);
            setRoles([newProfile.role_text || 'free']);
            logger.log('[useProfileLoader] Profile created and set successfully for user:', userId);
            logAuthEvent('profile_created', { 
              userId,
              correlationId,
              component: 'useProfileLoader'
            });
          } else {
            criticalLogger.error('[useProfileLoader] Failed to create profile');
            setRoles(['free']);
            logAuthEvent('profile_creation_failed', { 
              userId,
              correlationId,
              component: 'useProfileLoader'
            });
          }
        } else {
          logger.log('[useProfileLoader] Profile loaded successfully:', {
            id: data.id,
            username: data.username,
            email: data.email,
            role: data.role_text,
            isSystemAccount: data.is_system_account
          });
          
          // Self-healing: if username is null/empty, compute and update it
          if (!data.username || data.username.trim() === '') {
            logger.log('[useProfileLoader] Username is null/empty, self-healing profile:', data.id);
            const fallbackUsername = data.email && !data.email.includes('@apple.private') 
              ? data.email.split('@')[0] 
              : `user_${data.id.slice(0, 8)}`;
            
            // Update the profile with the computed username
            supabase
              .from('profiles')
              .update({ username: fallbackUsername })
              .eq('id', data.id)
              .then(({ error }) => {
                if (error) {
                  logger.warn('[useProfileLoader] Failed to self-heal username:', error);
                } else {
                  logger.log('[useProfileLoader] Successfully self-healed username to:', fallbackUsername);
                  // Update local state
                  setProfile({ ...data, username: fallbackUsername });
                }
              });
          }
          
          // Check admin status using direct app_admins table query with known user ID
          const { data: adminCheck, error: adminError } = await supabase
            .from('app_admins')
            .select('user_id')
            .eq('user_id', session.user.id)
            .maybeSingle();

          if (adminError) {
            logger.log('[useProfileLoader] Admin check error (expected if not admin):', adminError);
            setIsAdminUser(false);
          } else {
            setIsAdminUser(Boolean(adminCheck));
          }

          logAuthEvent('profile_fetch_success', { 
            userId,
            correlationId,
            component: 'useProfileLoader'
          });
          setProfile(data);
          const userRole = data.role_text as RoleText;
          const rolesArray: RoleText[] = userRole ? [userRole] : ['free'];
          
          // Add admin role for backward compatibility if user is admin
          if (adminCheck) {
            rolesArray.push('staff_admin');
          }
          
          setRoles(rolesArray);
        }
        } catch (error: any) {
          // Handle critical errors that might cause sign-out
          if (error instanceof Error) {
            if (error.message?.includes('JWT') || 
                error.message?.includes('token') || 
                error.message?.includes('session')) {
              criticalLogger.error('[useProfileLoader] Auth-related error during profile fetch:', error);
              // Don't propagate auth errors that could trigger sign-out
              setRoles(['free']);
              setIsAdminUser(false);
              return;
            }
          }
          
          criticalLogger.error('[useProfileLoader] Unexpected error in profile fetch:', error);
          logAuthEvent('profile_fetch_error', { 
            userId, 
            error: error.message,
            correlationId,
            component: 'useProfileLoader'
          });
          setRoles(['free']);
          setIsAdminUser(false);
      } finally {
        // Clear timeout if operation completed
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        logger.log('[useProfileLoader] Profile loading complete, setting isLoadingProfile to false');
        setIsLoadingProfile(false);
      }
    };

    // Set timeout fallback for profile loading
    timeoutRef.current = setTimeout(() => {
      logger.warn('[useProfileLoader] ⏰ Profile fallback timeout - setting guest role after 8s');
      setIsLoadingProfile(false);
      setRoles(['free']);
      setIsAdminUser(false);
      timeoutRef.current = null;
    }, 8000);

    fetchOrCreateProfile();

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [session?.user, correlationId]);

  // 👇 PATCH: Retry profile load once if not present on first mount after session
  useEffect(() => {
    if (!session?.user?.id || isLoadingProfile) return;

    const timeout = setTimeout(() => {
      if (!profile) refreshProfile();
    }, 1000);

    return () => clearTimeout(timeout);
  }, [session?.user?.id, profile, isLoadingProfile]);

  // Set up real-time subscription for profile changes
  useEffect(() => {
    if (!session?.user?.id) return;

    logger.log('[useProfileLoader] Setting up profile subscription for user:', session.user.id);
    
    const subscription = supabase
      .channel('profile-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${session.user.id}`
        },
        (payload) => {
          logger.log('[useProfileLoader] Profile updated via subscription:', payload.new);
          setProfile(payload.new as Profile);
        }
      );
      
    safeSubscribe(subscription);

    return () => {
      logger.log('[useProfileLoader] Cleaning up profile subscription');
      subscription.unsubscribe();
    };
  }, [session?.user?.id]);

  return {
    profile,
    roles,
    isLoadingProfile,
    refreshProfile,
    isAdminUser
  };
};
